import { Label, Loading } from "@components";
import dayjs from "dayjs";
import ComboFilters from "pages/Reports/ComboFilters";
import { ReportsAPI } from "providers/ReportsAPI";
import { useEffect, useMemo, useState } from "react";
import { If } from "shared/components/control-statements";
import { Currency } from "shared/utils/Currency";
import { Numbers } from "shared/utils/Numbers";
import styles from "styles/pages/reports/reports.module.scss";

export default function OperationsRecord({ isCashback, filters }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState({
    name: "Concessão",
    id: "CREDIT",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = Array.isArray(data.data) ? data.data : [];

  const totalPages = data.total_pages || 1;

  const provider = useMemo(() => new ReportsAPI(), []);

  useEffect(() => {
    fetchData();
  }, [filters, selectedOption, currentPage]);

  if (!data) return null;

  const formatPhone = (phone) => {
    if (!phone) return null;
    const match = phone.match(/^\+55\s?(\d{2})\s?(\d{5}-\d{4})$/);
    if (!match) return phone;
    const [, ddd, number] = match;
    return `(${ddd}) ${number}`;
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const response = await provider.loyaltyOperations(
      "operations",
      {
        ...filters,
        type: selectedOption.id,
        ...(filters.period === "FROM_THE_BEGINNING" && {
          start_date: null,
          end_date: null,
        }),
        ...(!filters.period && {
          start_date: dayjs().startOf("month").format("YYYY-MM-DD"),
          end_date: dayjs().endOf("month").format("YYYY-MM-DD"),
        }),
      },
      {
        page: currentPage,
      }
    );
    setData(response);
    setLoading(false);
  };

  return (
    <div className={styles.box2}>
      <div className="d-flex align-center justify-between px-20">
        <div className="d-flex align-center gap-5">
          <p className={styles.label}>Registro de operações</p>
        </div>

        <div className="d-flex align-center gap-10">
          <If
            cond={
              selectedOption?.id === "MANUAL_CREDIT" ||
              selectedOption?.id === "MANUAL_DEBIT"
            }
          >
            <Label className="mb-0">
              Operações manuais não são consideradas em outros gráficos deste
              relatório.
            </Label>
          </If>
          <ComboFilters
            options={[
              { name: "Concessão", id: "CREDIT" },
              { name: "Consumo", id: "DEBIT" },
              { name: "Expiração", id: "EXPIRATION" },
              { name: "Concessão manual", id: "MANUAL_CREDIT" },
              { name: "Consumo manual", id: "MANUAL_DEBIT" },
            ]}
            onSelect={(option) => setSelectedOption(option)}
            selectedOption={
              selectedOption || { name: "Concessão", id: "CREDIT" }
            }
          />
        </div>
      </div>

      <div className={styles.hairline} />

      <div className={styles.tableFull} style={{ minHeight: 525 }}>
        <If cond={loading}>
          <div className="flex-1 d-flex align-center justify-center">
            <Loading />
          </div>
        </If>
        <If cond={!loading}>
          <table className="w-100">
            <thead>
              <tr>
                <th>DATA E HORA</th>
                <th>TIPO DA OPERAÇÃO</th>
                <If
                  cond={["DEBIT", "MANUAL_DEBIT"].includes(selectedOption.id)}
                >
                  <th>CUPOM</th>
                </If>
                <th>
                  {isCashback ? "VALOR DA OPERAÇÃO" : "PONTOS DA OPERAÇÃO"}
                </th>
                <th>MARCA</th>
                <th>UNIDADE</th>
                <th>TELEFONE DO CLIENTE</th>
                <th>CADASTRO DO CLIENTE</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          <If cond={item.created_at}>
                            {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                          </If>
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          <If cond={["CREDIT"].includes(item.operation_type)}>
                            Concessão
                          </If>
                          <If
                            cond={["MANUAL_CREDIT"].includes(
                              item.operation_type
                            )}
                          >
                            Concessão manual
                          </If>
                          <If cond={["DEBIT"].includes(item.operation_type)}>
                            Consumo
                          </If>
                          <If
                            cond={["MANUAL_DEBIT"].includes(
                              item.operation_type
                            )}
                          >
                            Consumo manual
                          </If>
                          <If
                            cond={["EXPIRATION"].includes(item.operation_type)}
                          >
                            Expiração
                          </If>
                        </p>
                      </div>
                    </td>
                    <If
                      cond={["DEBIT", "MANUAL_DEBIT"].includes(
                        selectedOption.id
                      )}
                    >
                      <td>{item.coupon_code || "---"}</td>
                    </If>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {isCashback
                            ? Currency.formatCurrency(item.value || 0)
                            : `${Numbers.formatInt(item.value || 0)} pts`}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>{item.brand}</p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>{item.unit}</p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          {formatPhone(item.phone)}
                        </p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-center">
                        <p className={styles.value}>
                          <If cond={item.customer_created_at}>
                            {dayjs(item.customer_created_at).format(
                              "DD/MM/YYYY HH:mm"
                            )}
                          </If>
                        </p>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </If>

        <div className={styles.pagination}>
          <button
            disabled={currentPage === 1 || !totalPages}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            <i className="fal fa-arrow-left" />
          </button>

          <span className={styles.page}>
            Página <strong>{currentPage || 1}</strong> de{" "}
            <strong>{totalPages || 1}</strong>
          </span>

          <button
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            <i className="fal fa-arrow-right" />
          </button>
        </div>
      </div>
    </div>
  );
}
